import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usersApi, type UserSummary } from "@/lib/api-users";

export type UserDialogMode =
  | { kind: "create" }
  | { kind: "edit"; user: UserSummary };

export interface UserDialogProps {
  open: boolean;
  mode: UserDialogMode;
  onOpenChange: (open: boolean) => void;
}

export function UserDialog({ open, mode, onOpenChange }: UserDialogProps) {
  const qc = useQueryClient();
  const isCreate = mode.kind === "create";
  const initial = mode.kind === "edit" ? mode.user : null;

  const [username, setUsername] = React.useState(initial?.username ?? "");
  const [email, setEmail] = React.useState(initial?.email ?? "");
  const [password, setPassword] = React.useState("");
  const [fullName, setFullName] = React.useState(initial?.full_name ?? "");
  const [organization, setOrganization] = React.useState(initial?.organization ?? "");
  const [state, setState] = React.useState<"active" | "disabled">(initial?.state ?? "active");
  const [groups, setGroups] = React.useState<string[]>(initial?.groups ?? []);

  React.useEffect(() => {
    if (open) {
      setUsername(initial?.username ?? "");
      setEmail(initial?.email ?? "");
      setPassword("");
      setFullName(initial?.full_name ?? "");
      setOrganization(initial?.organization ?? "");
      setState(initial?.state ?? "active");
      setGroups(initial?.groups ?? []);
    }
  }, [open, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupsQuery = useQuery({
    queryKey: ["users", "groups", "all"],
    queryFn: () => usersApi.listGroups({ page_size: 100 }),
    enabled: open,
    select: (page) => page.items,
  });

  const mutation = useMutation({
    mutationFn: () => {
      if (mode.kind === "create") {
        return usersApi.create({
          username,
          email,
          password,
          full_name: fullName || undefined,
          organization: organization || undefined,
          groups,
        });
      }
      return usersApi.update(mode.user.user_id, {
        email: email !== mode.user.email ? email : undefined,
        full_name: fullName,
        organization: organization,
        state,
        groups,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      onOpenChange(false);
    },
  });

  function toggleGroup(gid: string) {
    setGroups((curr) =>
      curr.includes(gid) ? curr.filter((g) => g !== gid) : [...curr, gid],
    );
  }

  const valid =
    (isCreate
      ? /^[a-zA-Z0-9._-]+$/.test(username) && password.length >= 8
      : true) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {isCreate ? "New user" : `Edit ${initial?.username}`}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={!isCreate}
                autoFocus={isCreate}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          {isCreate && (
            <div className="space-y-1.5">
              <Label htmlFor="password">Initial password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
              <p className="text-xs text-muted-foreground">
                Min 8 characters. The user can change it after first login.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="full_name">Full name</Label>
              <Input
                id="full_name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="org">Organization</Label>
              <Input
                id="org"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
              />
            </div>
          </div>

          {!isCreate && (
            <div className="space-y-1.5">
              <Label>State</Label>
              <div className="flex gap-2">
                {(["active", "disabled"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setState(s)}
                    className={
                      "px-3 h-7 text-xs uppercase tracking-wider border " +
                      (state === s
                        ? "bg-accent text-accent-foreground border-accent"
                        : "border-border hover:bg-muted")
                    }
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Groups</Label>
            <div className="flex flex-wrap gap-2 rounded-md border border-border/60 bg-muted/30 p-2">
              {groupsQuery.data?.length ? (
                groupsQuery.data.map((g) => {
                  const selected = groups.includes(g.group_id);
                  return (
                    <button
                      key={g.group_id}
                      type="button"
                      onClick={() => toggleGroup(g.group_id)}
                      className={
                        "px-2 h-6 text-xs uppercase tracking-wider border " +
                        (selected
                          ? "bg-accent text-accent-foreground border-accent"
                          : "border-border hover:bg-muted text-muted-foreground")
                      }
                    >
                      {g.group_id}
                    </button>
                  );
                })
              ) : (
                <span className="text-xs text-muted-foreground">Loading groups…</span>
              )}
            </div>
          </div>

          {mutation.error && (
            <Alert tone="danger" title="Failed">
              {(mutation.error as Error).message}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!valid || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
            {isCreate ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
